import { supabase, isConfigured } from './supabase';

const INSTANTLY_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/instantly`;

interface InstantlyList {
  id: string;
  name: string;
  lead_count?: number;
}

interface InstantlyCampaign {
  id: string;
  name: string;
  status?: string;
}

interface InstantlyAnalytics {
  campaign_id: string;
  days: number;
  sent: number;
  opens: number;
  replies: number;
  open_rate: number;
  reply_rate: number;
  bounces: number;
}

interface InstantlyOrgSettings {
  org_id: string;
  default_lead_list_id: string | null;
  default_campaign_id: string | null;
}

interface PushResult {
  success: boolean;
  instantly_lead_id?: string;
  list_id?: string;
  campaign_id?: string;
  error?: string;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

export async function pushContactToInstantly(
  orgId: string,
  contactId: string,
  leadListId?: string,
  campaignId?: string
): Promise<PushResult> {
  if (!isConfigured) return { success: false, error: 'Not configured' };

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${INSTANTLY_FUNCTION_URL}/push`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        org_id: orgId,
        contact_id: contactId,
        lead_list_id: leadListId,
        campaign_id: campaignId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Push failed' };
    }

    return data as PushResult;
  } catch (error) {
    console.error('[instantly] Push failed:', error);
    return { success: false, error: (error as Error).message };
  }
}

export async function getInstantlyLists(): Promise<InstantlyList[]> {
  if (!isConfigured) return [];

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${INSTANTLY_FUNCTION_URL}/lists`, { headers });

    if (!response.ok) {
      console.error('[instantly] Lists fetch failed:', response.status);
      return [];
    }

    const data = await response.json();
    return (data.items || data || []) as InstantlyList[];
  } catch (error) {
    console.error('[instantly] Lists fetch failed:', error);
    return [];
  }
}

export async function getInstantlyCampaigns(): Promise<InstantlyCampaign[]> {
  if (!isConfigured) return [];

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${INSTANTLY_FUNCTION_URL}/campaigns`, { headers });

    if (!response.ok) {
      console.error('[instantly] Campaigns fetch failed:', response.status);
      return [];
    }

    const data = await response.json();
    return (data.items || data || []) as InstantlyCampaign[];
  } catch (error) {
    console.error('[instantly] Campaigns fetch failed:', error);
    return [];
  }
}

export async function getInstantlyAnalytics(
  campaignId: string,
  days = 7
): Promise<InstantlyAnalytics | null> {
  if (!isConfigured) return null;

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${INSTANTLY_FUNCTION_URL}/analytics?campaign_id=${campaignId}&days=${days}`,
      { headers }
    );

    if (!response.ok) {
      console.error('[instantly] Analytics fetch failed:', response.status);
      return null;
    }

    return await response.json() as InstantlyAnalytics;
  } catch (error) {
    console.error('[instantly] Analytics fetch failed:', error);
    return null;
  }
}

export async function getInstantlyOrgSettings(orgId: string): Promise<InstantlyOrgSettings | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_instantly_org_settings')
    .select('*')
    .eq('org_id', orgId)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('[instantly] Settings fetch failed:', error);
  }

  return data as InstantlyOrgSettings | null;
}

export async function saveInstantlyOrgSettings(
  orgId: string,
  defaultLeadListId: string | null,
  defaultCampaignId: string | null
): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_instantly_org_settings')
    .upsert({
      org_id: orgId,
      default_lead_list_id: defaultLeadListId,
      default_campaign_id: defaultCampaignId,
      updated_at: new Date().toISOString(),
    });

  if (error) {
    console.error('[instantly] Settings save failed:', error);
    return false;
  }

  return true;
}

export async function getInstantlyMapping(contactId: string): Promise<{
  instantly_lead_id: string | null;
  last_pushed_at: string | null;
} | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase.rpc('get_instantly_mapping', {
    p_contact_id: contactId,
  });

  if (error) {
    console.error('[instantly] Mapping fetch failed:', error);
    return null;
  }

  if (data && data.length > 0) {
    return data[0];
  }

  return null;
}

export type { InstantlyList, InstantlyCampaign, InstantlyAnalytics, InstantlyOrgSettings, PushResult };
